import { MapPin } from "lucide-react";
import type { MapMouseEvent } from "maplibre-gl";
import { useCallback, useEffect, useRef } from "react";
import {
  Map,
  MapControls,
  MapMarker,
  MarkerContent,
  useMap,
  type MapRef,
} from "@/components/ui/map";
import { DEFAULT_CENTER, DEFAULT_ZOOM } from "@/lib/maps/mapcn";

type Props = {
  lat: number | null;
  lng: number | null;
  onChange: (next: { lat: number; lng: number }) => void;
  onLoadError?: (error: Error) => void;
  onLocateError?: (status: "denied" | "unavailable" | "timeout" | "unsupported") => void;
  className?: string;
};

export function AddressMapPicker({
  lat,
  lng,
  onChange,
  onLoadError,
  onLocateError,
  className,
}: Props) {
  const mapRef = useRef<MapRef | null>(null);
  const onChangeRef = useRef(onChange);
  const selected = {
    lat: lat ?? DEFAULT_CENTER.lat,
    lng: lng ?? DEFAULT_CENTER.lng,
  };

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (lat == null || lng == null) {
      return;
    }
    const current = map.getCenter();
    if (current.lat === lat && current.lng === lng) {
      return;
    }
    map.easeTo({ center: [lng, lat], duration: 400 });
  }, [lat, lng]);

  const handleSelect = useCallback((next: { lat: number; lng: number }) => {
    onChangeRef.current(next);
  }, []);

  return (
    <div className={className}>
      <div className="h-72 w-full overflow-hidden rounded-md border bg-muted">
        <Map
          ref={mapRef}
          center={[selected.lng, selected.lat]}
          zoom={DEFAULT_ZOOM}
          onLoadError={onLoadError}
        >
          <AddressMapClickHandler onSelect={handleSelect} />
          <MapMarker
            draggable
            longitude={selected.lng}
            latitude={selected.lat}
            onDragEnd={handleSelect}
          >
            <MarkerContent>
              <MapPin className="size-8 -translate-y-3 fill-primary text-primary-foreground drop-shadow-md" />
            </MarkerContent>
          </MapMarker>
          <MapControls
            position="top-right"
            showLocate
            showFullscreen
            onLocate={({ latitude, longitude }) => handleSelect({ lat: latitude, lng: longitude })}
            onLocateError={onLocateError}
          />
        </Map>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Hacé click en el mapa o arrastrá el pin para ubicar el domicilio.
      </p>
    </div>
  );
}

function AddressMapClickHandler({
  onSelect,
}: {
  onSelect: (next: { lat: number; lng: number }) => void;
}) {
  const { map } = useMap();

  useEffect(() => {
    if (!map) {
      return;
    }

    const handleClick = (event: MapMouseEvent) => {
      onSelect({ lat: event.lngLat.lat, lng: event.lngLat.lng });
    };

    map.on("click", handleClick);

    return () => {
      map.off("click", handleClick);
    };
  }, [map, onSelect]);

  return null;
}
